<template>
  <canvas ref="cv" class="tech-canvas"></canvas>
</template>

<script>
/**
 * 首屏科技感粒子网络画布
 * - 粒子之间按距离连线，鼠标移动时产生吸附与高亮
 * - 尊重 prefers-reduced-motion，低端设备自动降级
 */
export default {
  name: 'TechCanvas',
  props: {
    density: { type: Number, default: 15000 }, // 每个粒子占用的像素面积
    color: { type: String, default: '34, 224, 255' },
    linkColor: { type: String, default: '61, 123, 255' }
  },
  data() {
    return { raf: 0 }
  },
  mounted() {
    this.canvas = this.$refs.cv
    this.ctx = this.canvas.getContext('2d')
    this.particles = []
    this.mouse = { x: -9999, y: -9999 }
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.resize()
    window.addEventListener('resize', this.resize)
    window.addEventListener('mousemove', this.onMove)
    this.loop()
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('mousemove', this.onMove)
  },
  methods: {
    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const rect = this.canvas.getBoundingClientRect()
      this.w = rect.width || window.innerWidth
      this.h = rect.height || window.innerHeight
      this.canvas.width = this.w * dpr
      this.canvas.height = this.h * dpr
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this.build()
    },
    build() {
      const count = Math.max(26, Math.min(120, Math.round((this.w * this.h) / this.density)))
      this.particles = Array.from({ length: count }, () => ({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
        r: Math.random() * 1.6 + 0.7
      }))
    },
    onMove(e) {
      const rect = this.canvas.getBoundingClientRect()
      this.mouse.x = e.clientX - rect.left
      this.mouse.y = e.clientY - rect.top
    },
    loop() {
      this.raf = requestAnimationFrame(this.loop)
      const { ctx, w, h, particles, mouse } = this
      ctx.clearRect(0, 0, w, h)

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]
        if (!this.reduced) {
          p.x += p.vx
          p.y += p.vy
        }
        if (p.x < 0 || p.x > w) p.vx *= -1
        if (p.y < 0 || p.y > h) p.vy *= -1
        p.x = Math.max(0, Math.min(w, p.x))
        p.y = Math.max(0, Math.min(h, p.y))

        // 鼠标吸附
        const dxm = mouse.x - p.x
        const dym = mouse.y - p.y
        const dm = Math.hypot(dxm, dym)
        if (dm < 170) {
          p.x += dxm * 0.0016
          p.y += dym * 0.0016
        }

        ctx.beginPath()
        ctx.fillStyle = `rgba(${this.color}, ${dm < 170 ? 0.9 : 0.55})`
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fill()
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i]
          const b = particles[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const d2 = dx * dx + dy * dy
          if (d2 > 26000) continue
          const alpha = (1 - d2 / 26000) * 0.42
          ctx.strokeStyle = `rgba(${this.linkColor}, ${alpha})`
          ctx.lineWidth = 0.7
          ctx.beginPath()
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
          ctx.stroke()
        }
      }
    }
  }
}
</script>

<style scoped>
.tech-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
